import React, { useState, useEffect } from 'react';
import { Mail, Send, Loader2, User, AtSign, Clock, Calendar, FileText } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function SendEmailModal({ 
  isOpen, 
  onClose, 
  formData, 
  quote, 
  formatPrice, 
  formatDuration, 
  distributionAddon, 
  booktrailerAddon,
  legalKitAddon,
  coverDesignAddon,
  brandingPackAddon,
  musicStockAddon,
  consultoriaAddon,
  qrDinamicoAddon,
  enlaceGoAddon,
  nfcVcardAddon,
  separadoresAddon,
  preventaAddon,
  bannerPromotionAddon,
  disenoWebBannerAddon,
  photoshootAddon,
  paginaAutorAddon,
  pautajeDigitalAddon,
  addonsTotal,
  voiceCloningCost = 0,
  extraVoicesUltraCost = 0,
  extraVoicesUltra = 0,
  multivozCost = 0,
  multivozVoces = 0,
  revisionAudioCost = 0,
  masterizacionCost = 0,
  podcastPackage,
  podcastAddons,
  podcastTotal,
  podcastQuantity = 1,
  podcastData = {},
  podcastPostProduction = false,
  estimatedRecordingHours = 0,
  walkingPodcastPackage = null,
  walkingPodcastTotal = 0,
  walkingPodcastQuantity = 1,
  walkingPodcastRecordingHours = 0,
  walkingSonidoDirectoTotal = 0,
  walkingAsistenteTotal = 0,
  rentaEquipoData = {},
  rentaEquipoTotal = 0,
  transmisionData = {},
  transmisionTotal = 0,
  sonidoDirectoData = {},
  sonidoDirectoTotal = 0,
  foroData = {},
  foroTotal = 0,
  interpretacionData = {},
  interpretacionTotal = 0,
  sonorizacionData = {},
  sonorizacionTotal = 0,
  projectName = '',
  projectCoverUrl = '',
  grandTotal,
  subtotalBeforeDiscount = 0,
  customDiscount = 0,
  customDiscountAmount = 0,
  subtotalAfterDiscount = 0,
  ivaAmount = 0,
  customerData,
  currency,
  exchangeRate = 17.5,
  savedQuoteCode = '',
  showCoproduccion = false,
  coproduccionPercent = 50,
  coproduccionAmount = 0,
  clientPayAmount = 0,
  viubuxConnectActive = false,
  viubuxConnectInscription = 0,
  viubuxConnectDiscountAmount = 0,
  vcCatalogoCost = 0,
  editorialData = null
}) {
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [ccEmails, setCcEmails] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [scheduleMode, setScheduleMode] = useState('now'); // 'now' or 'scheduled'
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [attachPdf, setAttachPdf] = useState(true); // Default to attach PDF

  // Auto-fill with customer data when modal opens
  useEffect(() => {
    if (isOpen && customerData) {
      setRecipientName(customerData.name || '');
      setRecipientEmail(customerData.email || '');
      setCcEmails('');
      setCustomMessage('');
      setScheduleMode('now');
      setScheduledDate('');
      setScheduledTime('');
      setAttachPdf(true);
    }
  }, [isOpen, customerData]);

  // Function to generate PDF as base64 for email attachment
  const generatePdfBase64 = async () => {
    const { exportQuoteToPDF } = await import('../utils/pdfExport');
    
    // Build the same data structure as QuotePreview uses
    const addonsData = [];
    if (distributionAddon?.price > 0) addonsData.push({ name: distributionAddon.name, price: distributionAddon.price, quantity: 1 });
    if (booktrailerAddon?.price > 0) addonsData.push({ name: booktrailerAddon.name, price: booktrailerAddon.price, quantity: 1 });
    if (legalKitAddon?.price > 0) addonsData.push({ name: legalKitAddon.name, price: legalKitAddon.price, quantity: 1 });
    if (coverDesignAddon?.price > 0) addonsData.push({ name: coverDesignAddon.name, price: coverDesignAddon.price, quantity: 1 });
    if (brandingPackAddon?.price > 0) addonsData.push({ name: brandingPackAddon.name, price: brandingPackAddon.price, quantity: 1 });
    if (musicStockAddon?.price > 0) addonsData.push({ name: musicStockAddon.name, price: musicStockAddon.price, quantity: musicStockAddon.quantity || 1 });
    if (consultoriaAddon?.price > 0) addonsData.push({ name: consultoriaAddon.name, price: consultoriaAddon.price, quantity: consultoriaAddon.quantity || 1 });
    if (paginaAutorAddon?.price > 0) addonsData.push({ name: paginaAutorAddon.name, price: paginaAutorAddon.price, quantity: 1 });
    if (pautajeDigitalAddon?.price > 0) addonsData.push({ name: pautajeDigitalAddon.name, price: pautajeDigitalAddon.price, quantity: 1 });

    const hasAudiobook = quote?.package?.name && (quote?.estimatedDurationHours || 0) > 0;
    const hasPodcast = podcastPackage?.price > 0;

    const quoteDataForPDF = {
      _returnBlob: false,
      bookTitle: formData?.bookTitle,
      customerName: recipientName,
      customerEmail: recipientEmail,
      customerPhone: customerData?.phone,
      coverUrl: formData?.coverUrl,
      projectName: projectName || null,
      audiobookData: hasAudiobook ? { packageName: quote?.package?.name, pricePerHour: quote?.package?.price, durationHours: quote?.estimatedDurationHours, subtotal: quote?.grandTotal, volumeDiscount: quote?.volumeDiscountPercent, manualDiscount: quote?.manualDiscountPercent } : null,
      podcastData: hasPodcast ? { packageName: podcastPackage?.name, quantity: podcastQuantity, recordingHours: estimatedRecordingHours, pricePerUnit: podcastPackage?.price, subtotal: podcastTotal } : null,
      walkingPodcastPackage: walkingPodcastPackage && walkingPodcastTotal > 0 ? walkingPodcastPackage : null,
      walkingPodcastData: walkingPodcastPackage && walkingPodcastTotal > 0 ? { packageName: walkingPodcastPackage?.name, quantity: walkingPodcastQuantity, recordingHours: walkingPodcastRecordingHours, pricePerUnit: walkingPodcastPackage?.price, sonidoDirectoTotal: walkingSonidoDirectoTotal, asistenteTotal: walkingAsistenteTotal, subtotal: walkingPodcastTotal } : null,
      addonsData: addonsData.length > 0 ? addonsData : null,
      editorialData: editorialData && editorialData.subtotal > 0 ? editorialData : null,
      rentaEquipoData: rentaEquipoTotal > 0 ? rentaEquipoData : null,
      rentaEquipoTotal,
      transmisionData: transmisionTotal > 0 ? transmisionData : null,
      transmisionTotal,
      sonidoDirectoData: sonidoDirectoTotal > 0 ? sonidoDirectoData : null,
      sonidoDirectoTotal,
      foroData: foroTotal > 0 ? foroData : null,
      foroTotal,
      interpretacionData: interpretacionTotal > 0 ? interpretacionData : null,
      interpretacionTotal,
      sonorizacionData: sonorizacionTotal > 0 ? sonorizacionData : null,
      sonorizacionTotal,
      quoteNumber: savedQuoteCode || null,
      totals: {
        subtotal: grandTotal / 1.16,
        iva: grandTotal - (grandTotal / 1.16),
        grandTotal,
        viubuxConnectActive,
        viubuxConnectDiscountAmount,
        customDiscount,
        customDiscountAmount: grandTotal * (customDiscount / 100),
        finalTotal: grandTotal - (grandTotal * (customDiscount / 100)),
        addonsSubtotal: addonsTotal
      },
      currency,
      exchangeRate
    };

    // Generate blob, then convert to base64
    const blobData = { ...quoteDataForPDF, _returnBlob: true };
    const blob = await exportQuoteToPDF(null, blobData);
    
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(',')[1]);
      reader.readAsDataURL(blob);
    });
  };

  const handleSend = async () => {
    if (!recipientEmail || !recipientName) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    setIsSending(true);

    try {
      const quoteData = {
        // Customer info
        recipientName,
        recipientEmail,
        ccEmails: ccEmails.split(',').map(e => e.trim()).filter(e => e),
        customMessage: customMessage.trim(),
        
        // Audiobook info
        bookTitle: formData?.bookTitle || '',
        authorName: formData?.authorName || '',
        pageCount: formData?.pageCount || 0,
        wordCount: formData?.wordCount || 0,
        coverUrl: formData?.coverUrl || '',
        
        // Audiobook calculations
        durationFormatted: quote ? formatDuration(quote.estimatedDurationMinutes) : '',
        estimatedDurationHours: quote?.estimatedDurationHours || 0,
        packageName: quote?.package?.name || '',
        packageDescription: quote?.package?.description || '',
        basePrice: quote?.basePrice || 0,
        volumeDiscountPercent: quote?.volumeDiscountPercent || 0,
        volumeDiscountAmount: quote?.volumeDiscountAmount || 0,
        manualDiscountPercent: quote?.manualDiscountPercent || 0,
        manualDiscountAmount: quote?.manualDiscountAmount || 0,
        totalSavings: quote?.totalSavings || 0,
        finalPrice: quote?.finalPrice || 0,
        audiobookGrandTotal: quote?.grandTotal || 0,
        
        // Audiobook addons
        distributionAddon: distributionAddon?.price > 0 ? { name: distributionAddon.name, price: distributionAddon.price } : null,
        booktrailerAddon: booktrailerAddon?.price > 0 ? { name: booktrailerAddon.name, price: booktrailerAddon.price } : null,
        legalKitAddon: legalKitAddon?.price > 0 ? { name: legalKitAddon.name, price: legalKitAddon.price } : null,
        coverDesignAddon: coverDesignAddon?.price > 0 ? { name: coverDesignAddon.name, price: coverDesignAddon.price } : null,
        brandingPackAddon: brandingPackAddon?.price > 0 ? { name: brandingPackAddon.name, price: brandingPackAddon.price } : null,
        musicStockAddon: musicStockAddon?.price > 0 ? { name: musicStockAddon.name, price: musicStockAddon.price } : null,
        consultoriaAddon: consultoriaAddon?.price > 0 ? { name: consultoriaAddon.name, price: consultoriaAddon.price } : null,
        qrDinamicoAddon: qrDinamicoAddon?.price > 0 ? { name: qrDinamicoAddon.name, price: qrDinamicoAddon.price } : null,
        enlaceGoAddon: enlaceGoAddon?.price > 0 ? { name: enlaceGoAddon.name, price: enlaceGoAddon.price } : null,
        nfcVcardAddon: nfcVcardAddon?.price > 0 ? { name: nfcVcardAddon.name, price: nfcVcardAddon.price } : null,
        separadoresAddon: separadoresAddon?.price > 0 ? { name: separadoresAddon.name, price: separadoresAddon.price } : null,
        preventaAddon: preventaAddon?.price > 0 ? { name: preventaAddon.name, price: preventaAddon.price } : null,
        bannerPromotionAddon: bannerPromotionAddon?.price > 0 ? { name: bannerPromotionAddon.name, price: bannerPromotionAddon.price } : null,
        disenoWebBannerAddon: disenoWebBannerAddon?.price > 0 ? { name: disenoWebBannerAddon.name, price: disenoWebBannerAddon.price } : null,
        photoshootAddon: photoshootAddon?.price > 0 ? { name: photoshootAddon.name, price: photoshootAddon.price } : null,
        paginaAutorAddon: paginaAutorAddon?.price > 0 ? { name: paginaAutorAddon.name, price: paginaAutorAddon.price } : null,
        pautajeDigitalAddon: pautajeDigitalAddon?.price > 0 ? { name: pautajeDigitalAddon.name, price: pautajeDigitalAddon.price } : null,
        addonsTotal: addonsTotal || 0,
        
        // New audiobook extras
        voiceCloningCost: voiceCloningCost || 0,
        extraVoicesUltraCost: extraVoicesUltraCost || 0,
        extraVoicesUltra: extraVoicesUltra || 0,
        multivozCost: multivozCost || 0,
        multivozVoces: multivozVoces || 0,
        revisionAudioCost: revisionAudioCost || 0,
        masterizacionCost: masterizacionCost || 0,
        
        // Podcast info
        podcastPackage: podcastPackage && podcastPackage.price > 0 ? { 
          name: podcastPackage.name, 
          price: podcastPackage.price,
          description: podcastPackage.description 
        } : null,
        podcastAddons: podcastAddons?.filter(a => a?.price > 0)?.map(a => ({ name: a.name, price: a.price })) || [],
        podcastTotal: podcastTotal || 0,
        podcastTitle: podcastData?.title || '',
        podcastQuantity: podcastQuantity || 1,
        podcastRecordingHours: estimatedRecordingHours || 0,
        podcastPostProduction: !!podcastPostProduction,
        
        // Walking Podcast
        walkingPodcastPackage: walkingPodcastPackage && walkingPodcastTotal > 0 ? {
          name: walkingPodcastPackage.name,
          price: walkingPodcastPackage.price
        } : null,
        walkingPodcastTotal: walkingPodcastTotal || 0,
        walkingPodcastQuantity: walkingPodcastQuantity || 1,
        walkingPodcastRecordingHours: walkingPodcastRecordingHours || 0,
        walkingSonidoDirectoTotal: walkingSonidoDirectoTotal || 0,
        walkingAsistenteTotal: walkingAsistenteTotal || 0,
        
        // New sections
        rentaEquipoData: rentaEquipoData || null,
        rentaEquipoTotal: rentaEquipoTotal || 0,
        transmisionData: transmisionData || null,
        transmisionTotal: transmisionTotal || 0,
        sonidoDirectoData: sonidoDirectoData || null,
        sonidoDirectoTotal: sonidoDirectoTotal || 0,
        foroData: foroData || null,
        foroTotal: foroTotal || 0,
        interpretacionData: interpretacionData || null,
        interpretacionTotal: interpretacionTotal || 0,
        sonorizacionData: sonorizacionData || null,
        sonorizacionTotal: sonorizacionTotal || 0,
        projectName: projectName || '',
        projectCoverUrl: projectCoverUrl || '',
        customerEmail: recipientEmail,
        
        // Discount and IVA info
        subtotalBeforeDiscount: subtotalBeforeDiscount || 0,
        customDiscount: customDiscount || 0,
        customDiscountAmount: customDiscountAmount || 0,
        subtotalAfterDiscount: subtotalAfterDiscount || 0,
        ivaAmount: ivaAmount || 0,
        
        // Grand total
        grandTotal: grandTotal || 0,
        currency: currency || 'MXN',
        exchangeRate: exchangeRate || 17.5,
        
        // Payment link
        depositAmount: (grandTotal || 0) * 0.10,
        paymentUrl: window.location.origin,
        
        // Co-Producción
        showCoproduccion: showCoproduccion,
        coproduccionPercent: coproduccionPercent,
        coproduccionAmount: coproduccionAmount,
        clientPayAmount: clientPayAmount,
        
        // ViubuxConnect
        viubuxConnectActive: viubuxConnectActive,
        viubuxConnectInscription: viubuxConnectInscription,
        viubuxConnectDiscountAmount: viubuxConnectDiscountAmount,
        vcCatalogoCost: vcCatalogoCost,
        
        // Editorial Services
        editorialData: editorialData && editorialData.subtotal > 0 ? editorialData : null,
        
        // CSF (Constancia de Situación Fiscal)
        fiscalUrl: customerData?.fiscalUrl || null,
        quoteNumber: savedQuoteCode || null
      };

      // Generate PDF base64 if attach option is enabled
      let pdfData = null;
      if (attachPdf) {
        try {
          pdfData = await generatePdfBase64();
        } catch (pdfError) {
          console.warn('Could not generate PDF:', pdfError);
          // Continue without PDF
        }
      }

      await axios.post(`${API}/send-quote-email`, {
        recipient_email: recipientEmail,
        recipient_name: recipientName,
        quote_data: quoteData,
        attach_pdf: attachPdf,
        pdf_data: pdfData
      });

      toast.success(attachPdf ? 'Cotización enviada con PDF adjunto' : 'Cotización enviada exitosamente', {
        description: `Se ha enviado a ${recipientEmail}`
      });
      
      onClose();
    } catch (error) {
      console.error('Error sending email:', error);
      const errorMessage = error.response?.data?.detail || 'Error al enviar el correo';
      toast.error(errorMessage);
    } finally {
      setIsSending(false);
    }
  };

  const handleSchedule = async () => {
    if (!recipientEmail || !recipientName) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    if (!scheduledDate || !scheduledTime) {
      toast.error('Por favor selecciona fecha y hora para el envío');
      return;
    }

    setIsSending(true);

    try {
      const quoteData = {
        recipientName,
        recipientEmail,
        ccEmails: ccEmails.split(',').map(e => e.trim()).filter(e => e),
        customMessage: customMessage.trim(),
        bookTitle: formData?.bookTitle || '',
        authorName: formData?.authorName || '',
        pageCount: formData?.pageCount || 0,
        wordCount: formData?.wordCount || 0,
        coverUrl: formData?.coverUrl || '',
        durationFormatted: quote ? formatDuration(quote.estimatedDurationMinutes) : '',
        estimatedDurationHours: quote?.estimatedDurationHours || 0,
        packageName: quote?.package?.name || '',
        packageDescription: quote?.package?.description || '',
        basePrice: quote?.basePrice || 0,
        volumeDiscountPercent: quote?.volumeDiscountPercent || 0,
        volumeDiscountAmount: quote?.volumeDiscountAmount || 0,
        manualDiscountPercent: quote?.manualDiscountPercent || 0,
        manualDiscountAmount: quote?.manualDiscountAmount || 0,
        totalSavings: quote?.totalSavings || 0,
        finalPrice: quote?.finalPrice || 0,
        audiobookGrandTotal: quote?.grandTotal || 0,
        distributionAddon: distributionAddon?.price > 0 ? { name: distributionAddon.name, price: distributionAddon.price } : null,
        booktrailerAddon: booktrailerAddon?.price > 0 ? { name: booktrailerAddon.name, price: booktrailerAddon.price } : null,
        legalKitAddon: legalKitAddon?.price > 0 ? { name: legalKitAddon.name, price: legalKitAddon.price } : null,
        coverDesignAddon: coverDesignAddon?.price > 0 ? { name: coverDesignAddon.name, price: coverDesignAddon.price } : null,
        brandingPackAddon: brandingPackAddon?.price > 0 ? { name: brandingPackAddon.name, price: brandingPackAddon.price } : null,
        musicStockAddon: musicStockAddon?.price > 0 ? { name: musicStockAddon.name, price: musicStockAddon.price } : null,
        consultoriaAddon: consultoriaAddon?.price > 0 ? { name: consultoriaAddon.name, price: consultoriaAddon.price } : null,
        qrDinamicoAddon: qrDinamicoAddon?.price > 0 ? { name: qrDinamicoAddon.name, price: qrDinamicoAddon.price } : null,
        enlaceGoAddon: enlaceGoAddon?.price > 0 ? { name: enlaceGoAddon.name, price: enlaceGoAddon.price } : null,
        nfcVcardAddon: nfcVcardAddon?.price > 0 ? { name: nfcVcardAddon.name, price: nfcVcardAddon.price } : null,
        separadoresAddon: separadoresAddon?.price > 0 ? { name: separadoresAddon.name, price: separadoresAddon.price } : null,
        preventaAddon: preventaAddon?.price > 0 ? { name: preventaAddon.name, price: preventaAddon.price } : null,
        bannerPromotionAddon: bannerPromotionAddon?.price > 0 ? { name: bannerPromotionAddon.name, price: bannerPromotionAddon.price } : null,
        disenoWebBannerAddon: disenoWebBannerAddon?.price > 0 ? { name: disenoWebBannerAddon.name, price: disenoWebBannerAddon.price } : null,
        photoshootAddon: photoshootAddon?.price > 0 ? { name: photoshootAddon.name, price: photoshootAddon.price } : null,
        paginaAutorAddon: paginaAutorAddon?.price > 0 ? { name: paginaAutorAddon.name, price: paginaAutorAddon.price } : null,
        pautajeDigitalAddon: pautajeDigitalAddon?.price > 0 ? { name: pautajeDigitalAddon.name, price: pautajeDigitalAddon.price } : null,
        addonsTotal: addonsTotal || 0,
        podcastPackage: podcastPackage && podcastPackage.price > 0 ? { 
          name: podcastPackage.name, 
          price: podcastPackage.price,
          description: podcastPackage.description 
        } : null,
        podcastAddons: podcastAddons?.filter(a => a?.price > 0)?.map(a => ({ name: a.name, price: a.price })) || [],
        podcastTotal: podcastTotal || 0,
        podcastTitle: podcastData?.title || '',
        podcastQuantity: podcastQuantity || 1,
        podcastRecordingHours: estimatedRecordingHours || 0,
        podcastPostProduction: !!podcastPostProduction,
        customerEmail: recipientEmail,
        subtotalBeforeDiscount: subtotalBeforeDiscount || 0,
        customDiscount: customDiscount || 0,
        customDiscountAmount: customDiscountAmount || 0,
        subtotalAfterDiscount: subtotalAfterDiscount || 0,
        ivaAmount: ivaAmount || 0,
        grandTotal: grandTotal || 0,
        currency: currency || 'MXN',
        exchangeRate: exchangeRate || 17.5,
        depositAmount: (grandTotal || 0) * 0.10,
        paymentUrl: window.location.origin,
        // Co-Producción
        showCoproduccion: showCoproduccion,
        coproduccionPercent: coproduccionPercent,
        coproduccionAmount: coproduccionAmount,
        clientPayAmount: clientPayAmount,
        // ViubuxConnect
        viubuxConnectActive: viubuxConnectActive,
        viubuxConnectInscription: viubuxConnectInscription,
        viubuxConnectDiscountAmount: viubuxConnectDiscountAmount,
        vcCatalogoCost: vcCatalogoCost,
        fiscalUrl: customerData?.fiscalUrl || null,
        quoteNumber: savedQuoteCode || null
      };

      // Combine date and time into ISO format
      const scheduledDateTime = new Date(`${scheduledDate}T${scheduledTime}`).toISOString();

      const response = await axios.post(`${API}/scheduled-emails`, {
        recipient_email: recipientEmail,
        recipient_name: recipientName,
        quote_data: quoteData,
        scheduled_time: scheduledDateTime
      });

      toast.success('Email programado exitosamente', {
        description: response.data.message
      });
      
      onClose();
    } catch (error) {
      console.error('Error scheduling email:', error);
      const errorMessage = error.response?.data?.detail || 'Error al programar el correo';
      toast.error(errorMessage);
    } finally {
      setIsSending(false);
    }
  };

  const hasAudiobook = formData?.wordCount > 0 || formData?.pageCount > 0;
  const hasPodcast = podcastPackage && podcastPackage.price > 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#0f133a] border-[#272b96] text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            <Mail className="w-5 h-5 text-[#aa2aff]" />
            Enviar Cotización por Email
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Quote summary */}
          <div className="p-4 bg-[#080c2d]/50 rounded-lg border border-white/5">
            <p className="text-sm text-slate-400 mb-2">Resumen de cotización:</p>
            
            {hasAudiobook && (
              <div className="mb-2">
                <p className="font-semibold text-white">{formData.bookTitle || 'Audiolibro'}</p>
                <p className="text-xs text-slate-400">Paquete {quote?.package?.name}</p>
              </div>
            )}
            
            {hasPodcast && (
              <div className="mb-2">
                <p className="font-semibold text-white">Podcast {podcastPackage.name}</p>
              </div>
            )}
            
            <div className="border-t border-white/10 pt-2 mt-2">
              <p className="text-lg font-bold text-[#aa2aff]">
                Total: {formatPrice(grandTotal || 0)} {currency}
              </p>
              <p className="text-xs text-slate-400">
                Anticipo 10%: {formatPrice((grandTotal || 0) * 0.10)} {currency}
              </p>
            </div>
          </div>

          {/* Recipient name */}
          <div className="space-y-2">
            <Label htmlFor="recipientName" className="text-slate-300 flex items-center gap-2">
              <User className="w-4 h-4 text-[#aa2aff]" />
              Nombre del destinatario
            </Label>
            <Input
              id="recipientName"
              data-testid="input-recipient-name"
              placeholder="Ej: Juan Pérez"
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              className="bg-[#080c2d]/50 border-[#272b96] text-white placeholder:text-slate-500 focus:border-[#aa2aff] focus:ring-1 focus:ring-[#aa2aff]"
            />
          </div>

          {/* Recipient email */}
          <div className="space-y-2">
            <Label htmlFor="recipientEmail" className="text-slate-300 flex items-center gap-2">
              <AtSign className="w-4 h-4 text-[#aa2aff]" />
              Correo electrónico
            </Label>
            <Input
              id="recipientEmail"
              type="email"
              data-testid="input-recipient-email"
              placeholder="cliente@ejemplo.com"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              className="bg-[#080c2d]/50 border-[#272b96] text-white placeholder:text-slate-500 focus:border-[#aa2aff] focus:ring-1 focus:ring-[#aa2aff]"
            />
          </div>

          {/* CC emails */}
          <div className="space-y-2">
            <Label htmlFor="ccEmails" className="text-slate-300 flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#aa2aff]" />
              CC: Copias (opcional)
            </Label>
            <Input
              id="ccEmails"
              type="text"
              data-testid="input-cc-emails"
              placeholder="email1@ejemplo.com, email2@ejemplo.com"
              value={ccEmails}
              onChange={(e) => setCcEmails(e.target.value)}
              className="bg-[#080c2d]/50 border-[#272b96] text-white placeholder:text-slate-500 focus:border-[#aa2aff] focus:ring-1 focus:ring-[#aa2aff]"
            />
            <p className="text-xs text-slate-500">Separa múltiples correos con comas</p>
          </div>

          {/* Custom message */}
          <div className="space-y-2">
            <Label htmlFor="customMessage" className="text-slate-300 flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#aa2aff]" />
              Mensaje personalizado (opcional)
            </Label>
            <textarea
              id="customMessage"
              data-testid="input-custom-message"
              placeholder="Escribe un mensaje personalizado para el cliente..."
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              rows={3}
              className="w-full bg-[#080c2d]/50 border border-[#272b96] text-white placeholder:text-slate-500 focus:border-[#aa2aff] focus:ring-1 focus:ring-[#aa2aff] rounded-md px-3 py-2 text-sm resize-none"
            />
          </div>

          {/* Attach PDF option */}
          <div className="flex items-center space-x-3 p-3 bg-[#080c2d]/30 rounded-lg border border-[#272b96]/50">
            <Checkbox
              id="attachPdf"
              data-testid="checkbox-attach-pdf"
              checked={attachPdf}
              onCheckedChange={(checked) => setAttachPdf(checked)}
              className="border-[#aa2aff] data-[state=checked]:bg-[#aa2aff] data-[state=checked]:border-[#aa2aff]"
            />
            <Label htmlFor="attachPdf" className="text-slate-300 flex items-center gap-2 cursor-pointer">
              <FileText className="w-4 h-4 text-[#aa2aff]" />
              Adjuntar cotización en PDF
            </Label>
          </div>

          <Button
            onClick={handleSend}
            disabled={isSending || !recipientEmail || !recipientName}
            className="w-full bg-[#aa2aff] hover:bg-[#7012b2] text-white mt-4"
            data-testid="btn-send-email"
          >
            {isSending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Enviar Cotización
              </>
            )}
          </Button>

          <p className="text-xs text-slate-500 text-center">
            El cliente recibirá un correo con el detalle de la cotización y un botón para pagar el anticipo.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default SendEmailModal;
